import React from 'react';
import {
  FunctionField,
  Identifier,
  ListProps,
  RowClickFunction,
  TextField,
  useRedirect,
} from 'react-admin';
import { useListContext } from 'ra-core';

import {
  TextInput,
  Grid,
  GridList,
  Forbidden,
  DateField,
  SelectArrayInput,
  FileField,
  SelectInput,
  DateRangeSelect, MoneyField,
} from '@components';
import { CxInvoiceStatusField } from './CxInvoiceStatusField';
import { useExporter, useRbacEdit, useRbacList } from '@hooks';
import { CxInvoiceListActions } from '@pages/cx-invoice/CxInvoiceListActions';
import { CxInvoiceUpdatedByField } from '@pages/cx-invoice/CxInvoiceUpdatedByField';
import styles from '@pages/credit/CreditList.module.css';
import { CreditStatusField } from '@pages/credit/CreditStatusField';
import { CreditStatus } from '@types';
import { ColoredOption } from '@pages/credit-risk/RiskStateField';
import { CxInvoiceBooleanStatus } from '@pages/cx-invoice/CxInvoiceBooleanStatus';
import { moneyFormatter } from '@utils';

const creditStatusOptions: ColoredOption[] = [
  { id: CreditStatus.MODERATION, name: 'Applying', color: '#2196F3' },
  { id: CreditStatus.COMPLETED_FILE, name: 'Completed File', color: '#213df3' },
  { id: CreditStatus.BANK_REVISION, name: 'Analysis', color: '#009688' },
  { id: CreditStatus.APPROVED, name: 'Approved', color: '#4CAF50' },
  { id: CreditStatus.DENIED, name: 'Denied', color: '#FF5722' },
  { id: CreditStatus.SIGNED, name: 'Signed', color: '#4CAF50' },
  { id: CreditStatus.REPAID, name: 'Repaid', color: '#3F51B5' },
  { id: CreditStatus.RESTRUCTURE, name: 'Restructure', color: '#673AB7' },
  { id: CreditStatus.DELAY, name: 'Delay', color: '#CDDC39' },
];

type InvoiceNumberValue = number | string | null | undefined;

interface CxInvoiceListRecord {
  id: Identifier;
  finalAmount?: InvoiceNumberValue;
  totalPositionsIva?: InvoiceNumberValue;
}
export const CxInvoiceList = (props: ListProps) => {
  const rbacList = useRbacList();
  const rbacEdit = useRbacEdit();
  const redirect = useRedirect();
  const exporter = useExporter({
    filename: 'Invoices',
    props: {
      'creditCode': {
        label: 'Credit',
      },
      'invoiceAccredited': {
        label: 'Accredited name',
      },
      'invoiceNumber': {
        label: 'Invoice Number',
      },
      'creditStatus': {
        label: 'Credit Status',
        transform: (record: string) => (
          `${record[0].toUpperCase()}${record.slice(1)}`
        ),
      },
      'financeAnalysisStatus': {
        label: 'Finance Status',
        transform: 'boolean',
      },
      'cxAnalysisStatus': {
        label: 'CX Status',
        transform: 'boolean',
      },
      'status': {
        prop: 'statusName',
        label: 'Invoice Status',
      },
      'format': {
        label: 'Format',
      },
      'uploadedBy': {
        label: 'Uploaded by',
        transform: (record: any) => (
          `${record?.type ? record?.type + ': ' : ''}${record?.name || ''}`
        ),
      },
      'retailerName': {
        label: 'Retail Store',
      },
      'xmlUuid': {
        label: 'UUID',
      },
      'comment': {
        label: 'Invoice note',
      },
      'createdAt': {
        label: 'Date of upload',
        transform: 'date',
      },
      'xmlDate': {
        label: 'Fecha de timbrado',
        transform: 'date',
      },
      'total': {
        label: 'Sub-total',
        transform: 'money',
      },
      'totalIva': {
        label: 'Total',
        transform: 'money',
      },
    },
  });

  const onRowClick: RowClickFunction = (id: Identifier, _: string, record: any) => {
    redirect(`/credit/${record.creditId}?invoices=${id}`);
    return false;
  };

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <GridList
      {...props}
      exporter={exporter}
      editComponent={rbacEdit ? <CxInvoiceListActions /> : undefined}
      sort={{ field: 'createdAt', order: 'DESC' }}
      mergeButton
      summary={<Summary />}
      filters={[
        <TextInput source="q" key="q" variant="outlined" label="Search" alwaysOn />,
        <SelectArrayInput
          label="Invoice Status"
          key="status"
          source="status"
          choices={[
            { id: 'pending', name: 'Pending', color: '#888' },
            { id: 'approved', name: 'Approved', color: '#36BE29' },
            { id: 'denied', name: 'Denied', color: '#DD482E' },
          ]}
          alwaysOn
          className="MS-field"
          style={{ width: 280 }}
        />,
        <SelectArrayInput
          label="Credit Status"
          key="creditStatus"
          source="creditStatus"
          choices={creditStatusOptions}
          className="MS-field"
          style={{ width: 230 }}
        />,
        <DateRangeSelect
          key="date"
          source="date"
          label="Date of upload/Fecha de timbrado"
          labelClass={styles.labelClass}
          alwaysOn
          choices={[
            { id: 'createdAt', name: 'Date of upload' },
            { id: 'xmlDate', name: 'Fecha de timbrado' },
          ]}
        />,
        <SelectInput
          key="format"
          source="format"
          label="Format"
          alwaysOn
          choices={[
            { id: 'pdf', name: 'PDF' },
            { id: 'xml', name: 'XML' },
            { id: 'other', name: 'Other files' },
          ]}
          style={{ width: 200 }}
        />,
        <TextInput source="xmlUuid" key="xmlUuid" variant="outlined" label="UUID" alwaysOn />,
        <TextInput source="retailerName"
          key="retailerName"
          variant="outlined"
          label="Retail Store"
          alwaysOn />,
      ]}>
      <Grid
        rowClick={onRowClick}
        defaultColumns={
          'creditCode invoiceAccredited invoiceNumber status format createdAt total totalIva'
            .split(' ')}
      >
        <TextField source="creditCode" label="Credit" />
        <TextField source="invoiceAccredited" label="Accredited name" />
        <TextField source="invoiceNumber" label="Invoice Number" />
        <CreditStatusField source="creditStatus" label="Credit Status" />
        <CxInvoiceBooleanStatus source="financeAnalysisStatus" label="Finance Status" />
        <CxInvoiceBooleanStatus source="cxAnalysisStatus" label="CX Status" />
        <CxInvoiceStatusField source="status" label="Invoice Status" />
        <TextField source="format" label="Format" />
        <CxInvoiceUpdatedByField source="uploadedBy" label="Uploaded by" />
        <TextField source="retailerName" label="Retail Store" />
        <TextField source="xmlUuid" label="UUID" />
        <TextField source="comment" label="Invoice note" />
        <FileField source="file" label="Document" forList />
        <DateField source="createdAt" label="Date of upload" />
        <DateField source="xmlDate" label="Fecha de timbrado" />
        <MoneyField source="total" label="Sub-total" />
        <MoneyField source="finalAmount" label="Total" />
      </Grid>
    </GridList>
  );
};

const Summary = () => {
  const { data, selectedIds } = useListContext<CxInvoiceListRecord>();
  if (!data || selectedIds.length === 0) return null;

  const totalSum = data
    .filter((item) => selectedIds.includes(item.id))
    .reduce((sum, item) => sum + Number(item.finalAmount || 0), 0);

  return <span>Total Sum: {moneyFormatter.format(totalSum)}</span>;
};

